/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Full-height user-profile RIGHT PANEL (Figma node 2804:85635). Opened
// by tapping an @mention in the chat timeline. PURELY PRESENTATIONAL —
// the host (`apps/web/.../UserProfilePanelHost`) resolves the user from
// the directory + derives mutual chats and feeds this component plain,
// undefined-safe props. No SDK / identity / coordinate material flows
// through here.
//
// Layout (top → bottom):
//   • full-width colored COVER banner (~96px) + MoreVertical button
//   • ~64px squircle AVATAR overlapping the banner bottom, panel-ring
//   • header: name + verified badge / role, with Send + Add-contact
//     round action buttons on the right
//   • status row (emoji + bold text + muted "until")
//   • BIO card (elevated surface)
//   • company row (logo + name + verified, "View Company →" link)
//   • "Contact information" — email + phone (accent-brand links)
//   • "Mutual Chats" — list of shared groups (avatar + name + verified)
//
// Styling per the Tailwind-first rule: every static value is a
// className (design-token classes where one exists, arbitrary-value
// classes for exact Figma values without a token). The single inline
// `style` is the runtime-computed cover-banner color (a dynamic value a
// utility class cannot express).

import React from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import {
  ChevronRight,
  MoreVertical,
  Send,
  UserPlus,
} from 'lucide-react-native';
import { Avatar } from '../components/Avatar';
import { VerifiedBadge } from '../components/VerifiedBadge';

// Primary tint (`accent-brand`) — used for the Send button, links, and
// the "View Company" affordance. Mirrors the design-token hex so the
// lucide icon `color` prop (which needs a literal, not a class) stays in
// sync with the `accent-brand` className used elsewhere in the panel.
const ACCENT_BRAND = '#537EFF';
// Muted icon tint for the secondary (Add-contact / More) glyphs.
const ICON_MUTED = '#9CA3AF';

export interface UserProfileMutualChat {
  readonly id: string;
  readonly name: string;
  readonly avatarUri?: string | undefined;
  readonly verified?: boolean | undefined;
  readonly onPress?: (() => void) | undefined;
}

export interface UserProfileCompany {
  readonly name: string;
  readonly logoUri?: string | undefined;
  readonly verified?: boolean | undefined;
  readonly onViewCompany?: (() => void) | undefined;
}

export interface UserProfileStatus {
  readonly emoji?: string | undefined;
  readonly text: string;
  readonly until?: string | undefined;
}

export interface UserProfilePanelProps {
  readonly name: string;
  readonly role?: string | undefined;
  readonly verified?: boolean | undefined;
  readonly avatarUri?: string | undefined;
  /** Cover-banner color. Runtime-dynamic → the single inline style. */
  readonly coverColor?: string | undefined;
  readonly status?: UserProfileStatus | undefined;
  readonly bio?: string | undefined;
  readonly company?: UserProfileCompany | undefined;
  readonly email?: string | undefined;
  readonly phone?: string | undefined;
  readonly mutualChats?: ReadonlyArray<UserProfileMutualChat> | undefined;
  readonly onSend?: (() => void) | undefined;
  readonly onAddContact?: (() => void) | undefined;
  readonly onMore?: (() => void) | undefined;
  readonly onClose?: (() => void) | undefined;
}

// Default cover hue (olive/green from the Figma) when the host doesn't
// supply one. Kept as a const so the inline style always has a value.
const DEFAULT_COVER_COLOR = '#8A9A45';

export const UserProfilePanel = React.memo<UserProfilePanelProps>(
  ({
    name,
    role,
    verified,
    avatarUri,
    coverColor,
    status,
    bio,
    company,
    email,
    phone,
    mutualChats,
    onSend,
    onAddContact,
    onMore,
    onClose,
  }) => {
    const hasStatus = status !== undefined && status.text.length > 0;
    const hasMutuals = mutualChats !== undefined && mutualChats.length > 0;

    return (
      <ScrollView
        accessibilityLabel={`Profile for ${name}`}
        className="flex-1 h-full bg-light-surface-app dark:bg-dark-surface-app"
        contentContainerStyle={{ paddingBottom: 24 }}
      >
        {/* COVER banner — runtime-dynamic color is the documented inline
            fallback; everything else (height / full-width) is a class.
            The More button (and an optional Close on web) ride top-right. */}
        <View
          className="w-full h-24 relative"
          style={{ backgroundColor: coverColor ?? DEFAULT_COVER_COLOR }}
        >
          <View className="absolute top-3 right-3 flex-row items-center gap-2">
            {onClose !== undefined ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Close profile"
                onPress={onClose}
                className="w-8 h-8 items-center justify-center rounded-full active:opacity-70"
              >
                <Text className="text-white text-lg leading-none">×</Text>
              </Pressable>
            ) : null}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="More options"
              onPress={onMore}
              className="w-8 h-8 items-center justify-center rounded-full active:opacity-70"
            >
              <MoreVertical size={20} color="#FFFFFF" />
            </Pressable>
          </View>
        </View>

        <View className="px-4">
          {/* AVATAR squircle overlapping the banner bottom, with a ~3px
              panel-colored ring. Negative top margin pulls it up over the
              cover. The ring is a padded wrapper tinted with the panel bg. */}
          <View className="-mt-8 flex-row items-end justify-between">
            <View className="rounded-[19px] p-[3px] bg-light-surface-app dark:bg-dark-surface-app">
              <Avatar
                size="large"
                shape="rounded"
                initials={name}
                accessibilityLabel={name}
                {...(avatarUri !== undefined ? { imageUri: avatarUri } : {})}
              />
            </View>
            {/* Action buttons — Send (accent-brand) + Add-contact
                (elevated surface). Bottom-aligned to the avatar baseline. */}
            <View className="flex-row items-center gap-3 mb-1">
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Send a message to ${name}`}
                onPress={onSend}
                className="w-10 h-10 items-center justify-center rounded-full bg-light-accent-brand dark:bg-dark-accent-brand active:opacity-80"
              >
                <Send size={20} color="#FFFFFF" />
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Add ${name} to contacts`}
                onPress={onAddContact}
                className="w-10 h-10 items-center justify-center rounded-full bg-light-surface-elevated dark:bg-dark-surface-elevated active:opacity-80"
              >
                <UserPlus size={20} color={ICON_MUTED} />
              </Pressable>
            </View>
          </View>

          {/* Name + verified / role */}
          <View className="mt-3">
            <View className="flex-row items-center gap-1.5">
              <Text
                className="text-[20px] font-bold text-light-text-primary dark:text-dark-text-primary"
                numberOfLines={1}
              >
                {name}
              </Text>
              {verified === true ? <VerifiedBadge verified size={18} /> : null}
            </View>
            {role !== undefined && role.length > 0 ? (
              <Text
                className="mt-0.5 text-[14px] text-light-text-muted dark:text-dark-text-muted"
                numberOfLines={1}
              >
                {role}
              </Text>
            ) : null}
          </View>

          {/* Status row */}
          {hasStatus ? (
            <View className="mt-3 flex-row items-center gap-1.5">
              {status?.emoji !== undefined && status.emoji.length > 0 ? (
                <Text className="text-[14px]">{status.emoji}</Text>
              ) : null}
              <Text className="text-[14px] font-semibold text-light-text-primary dark:text-dark-text-primary">
                {status?.text}
              </Text>
              {status?.until !== undefined && status.until.length > 0 ? (
                <Text className="text-[14px] text-light-text-muted dark:text-dark-text-muted">
                  {status.until}
                </Text>
              ) : null}
            </View>
          ) : null}

          {/* BIO card */}
          {bio !== undefined && bio.length > 0 ? (
            <View className="mt-4 rounded-[12px] bg-light-surface-elevated dark:bg-dark-surface-elevated p-3.5">
              <Text className="text-[14px] leading-[20px] text-light-text-primary dark:text-dark-text-primary">
                {bio}
              </Text>
            </View>
          ) : null}

          {/* Company row */}
          {company !== undefined && company.name.length > 0 ? (
            <View className="mt-4 flex-row items-center">
              <Avatar
                size="company"
                shape="rounded"
                initials={company.name}
                accessibilityLabel={company.name}
                {...(company.logoUri !== undefined
                  ? { imageUri: company.logoUri }
                  : {})}
              />
              <View className="ml-2.5 flex-row items-center gap-1.5 flex-1">
                <Text
                  className="text-[15px] font-bold text-light-text-primary dark:text-dark-text-primary"
                  numberOfLines={1}
                >
                  {company.name}
                </Text>
                {company.verified === true ? (
                  <VerifiedBadge verified size={16} />
                ) : null}
              </View>
              <Pressable
                accessibilityRole="link"
                accessibilityLabel={`View ${company.name}`}
                onPress={company.onViewCompany}
                className="flex-row items-center gap-1 active:opacity-70"
              >
                <Text className="text-[14px] font-semibold text-light-accent-brand dark:text-dark-accent-brand">
                  View Company
                </Text>
                <ChevronRight size={16} color={ACCENT_BRAND} />
              </Pressable>
            </View>
          ) : null}

          {/* Contact information */}
          {email !== undefined || phone !== undefined ? (
            <View className="mt-5">
              <Text className="text-[13px] text-light-text-muted dark:text-dark-text-muted mb-2.5">
                Contact information
              </Text>
              <View className="border-t border-light-border-subtle dark:border-dark-border-subtle pt-2.5 gap-2">
                {email !== undefined && email.length > 0 ? (
                  <Text
                    accessibilityRole="link"
                    className="text-[14px] text-light-accent-brand dark:text-dark-accent-brand"
                    numberOfLines={1}
                  >
                    {email}
                  </Text>
                ) : null}
                {phone !== undefined && phone.length > 0 ? (
                  <Text
                    accessibilityRole="link"
                    className="text-[14px] text-light-accent-brand dark:text-dark-accent-brand"
                    numberOfLines={1}
                  >
                    {phone}
                  </Text>
                ) : null}
              </View>
            </View>
          ) : null}

          {/* Mutual Chats */}
          {hasMutuals ? (
            <View className="mt-5">
              <Text className="text-[13px] text-light-text-muted dark:text-dark-text-muted mb-2.5">
                Mutual Chats
              </Text>
              <View className="border-t border-light-border-subtle dark:border-dark-border-subtle pt-2.5 gap-3">
                {mutualChats?.map((chat) => (
                  <Pressable
                    key={chat.id}
                    accessibilityRole="button"
                    accessibilityLabel={chat.name}
                    onPress={chat.onPress}
                    className="flex-row items-center active:opacity-70"
                  >
                    <Avatar
                      size="tile"
                      shape="rounded"
                      initials={chat.name}
                      accessibilityLabel={chat.name}
                      {...(chat.avatarUri !== undefined
                        ? { imageUri: chat.avatarUri }
                        : {})}
                    />
                    <View className="ml-3 flex-row items-center gap-1.5 flex-1">
                      <Text
                        className="text-[15px] font-bold text-light-text-primary dark:text-dark-text-primary"
                        numberOfLines={1}
                      >
                        {chat.name}
                      </Text>
                      {chat.verified === true ? (
                        <VerifiedBadge verified size={16} />
                      ) : null}
                    </View>
                  </Pressable>
                ))}
              </View>
            </View>
          ) : null}
        </View>
      </ScrollView>
    );
  },
);

UserProfilePanel.displayName = 'UserProfilePanel';
